import { useAuthStore } from '../stores/authStore';
import { usePlatformStore } from '../stores/platformStore';
import { getCategoryFeatures, getIndustryLabel, type CategoryFeatures } from '../data/industryTaxonomy';
import { getScheduleConfig, type ScheduleConfig } from '../data/scheduleConfig';
import { useResolvedClusterConfig } from './useResolvedClusterConfig';

// Human-readable label per cluster — shown in the platform-admin lens badge.
const CLUSTER_LABELS: Record<string, string> = {
  A: 'Appointment',
  B: 'Field Service',
  C: 'Route-Based',
  D: 'Property',
  E: 'Event / Project',
  F: 'Automotive',
  G: 'Transport',
};

/**
 * Single source of truth for the active industry context.
 *
 * When a platform admin selects an industry lens, this hook returns
 * configs for that industry. Otherwise it returns configs for the
 * user's own business industry. All data queries remain scoped to
 * the admin's own business — this only affects UI configuration.
 */
export function useIndustryLens() {
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_platform_admin === true);
  const ownIndustry = useAuthStore((s) => s.business?.industry ?? null);
  const { industryLensActive, lensIndustryId, lensIndustryLabel } = usePlatformStore();

  // Effective industry: lens overrides own business when active
  const isLensActive = isPlatformAdmin && industryLensActive && lensIndustryId !== null;
  const categoryId = isLensActive ? lensIndustryId : ownIndustry;

  // Feature flags for the active industry
  const features: CategoryFeatures = getCategoryFeatures(categoryId);

  // Cluster routing — Industry Engine resolver over the cluster_config substrate
  const { config: clusterConfig } = useResolvedClusterConfig(categoryId);
  const clusterKey = clusterConfig.clusterKey;
  const clusterLabel = CLUSTER_LABELS[clusterKey] ?? '';

  // Schedule config (views, defaultView, slotDuration, resourceLabel, etc.)
  const scheduleConfig: ScheduleConfig = getScheduleConfig(categoryId);

  // Display label
  const industryName = isLensActive ? lensIndustryLabel : (categoryId ? getIndustryLabel(categoryId) : null);

  return {
    categoryId,
    isLensActive,
    isPlatformAdmin,
    industryName,
    features,
    clusterKey,
    clusterLabel,
    scheduleConfig,
  };
}
